/**
 * Content pages that lost a consolidation, and where they now point.
 *
 * A 6 September 2026 crawl found several clusters of pages chasing one query
 * in one town, each canonicalising to itself. Google picks one and reports the
 * rest as duplicates; worse, a customer reading two of them sees the site
 * contradict itself on price.
 *
 * These slugs live in `content_posts`, which is edited in Lovable, not here —
 * so they are retired at the route instead: /guides/<slug> and /blog/<slug>
 * 301 to the survivor, and the sitemap stops submitting them. Unpublishing the
 * row in Lovable later makes the entry a harmless no-op.
 *
 * Keyed by full path so guides and blog posts share one table, and kept as a
 * pure lookup so the rule is exercised by tests rather than restated in them.
 */
export const RETIRED_CONTENT_REDIRECTS: Record<string, string> = {
  // Oven cleaning: three pages, three different price tables for the same job
  // in the same town — £50–£120, £55–£140 and £45–£80. The survivor is the
  // one whose numbers agree with the money page: /services/oven-cleaning
  // quotes "Single ovens from £55, doubles from £75" and a £55–£130 range,
  // which is oven-cleaning-prices-southend and neither of the others.
  "/guides/oven-cleaning-costs-southend": "/guides/oven-cleaning-prices-southend",

  // End of tenancy checklist: the same two words in a different order. The
  // survivor is the one with a route file of its own and a sitemap entry.
  "/guides/end-of-tenancy-cleaning-checklist-southend":
    "/guides/end-of-tenancy-cleaning-southend-checklist",

  // Cleaning jobs: the blog post duplicates the dedicated hub slug for slug.
  // /guides/how-to-find-cleaning-jobs-in-southend deliberately survives — it
  // is the free guide the store funnel links to, with a job of its own.
  "/blog/cleaning-jobs-southend": "/cleaning-jobs-southend",
};

/** Where `path` should 301 to, or null if it is not retired. */
export function retiredContentTarget(path: string): string | null {
  return RETIRED_CONTENT_REDIRECTS[path] ?? null;
}

/** Slugs the sitemap must not submit, by kind. */
export function isRetiredContentSlug(kind: "blog" | "guide", slug: string): boolean {
  return `/${kind === "blog" ? "blog" : "guides"}/${slug}` in RETIRED_CONTENT_REDIRECTS;
}
